import { useAuthState } from "@/contexts/auth/auth.provider";
import { useRouter } from "next/router";
import { getPreviewToken } from "@/lib/paymentHelpers";
import { Card } from "@aqar-app/aqar-design-system";
import { Button, Modal } from "@aqar-app/react-ui";
import styles from "@/src/pages/payment-gateway/pricing-packages/.module.scss";

const ACTION_MAP = {
  subscribe: "اشترك",
  renew: "تجديد الإشتراك",
  upgrade: "ترقية الإشتراك",
};

const CheckIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={styles.icon}
  >
    <path
      d="M17.8174 18.1207C14.753 20.6222 5.18451 20.6222 2.18262 18.1207C-0.881788 15.6191 -0.569098 4.92494 2.18262 2.11069C4.93435 -0.703563 15.0657 -0.703563 17.8174 2.11069C20.5691 4.92494 20.8818 15.6191 17.8174 18.1207Z"
      fill="#E1E6EB"
    />
    <path
      d="M8.87183 14.3626C8.52846 14.3626 8.18513 14.2154 7.98893 13.9211L5.24213 10.3895C4.84972 9.89901 4.94784 9.21231 5.43833 8.8199C5.92883 8.42749 6.61553 8.52561 7.00794 9.0161L8.87183 11.4196L12.9921 6.0731C13.3845 5.5826 14.0712 5.48448 14.5617 5.87689C15.0522 6.2693 15.1503 6.956 14.7579 7.4465L9.75474 13.9211C9.50949 14.1664 9.2152 14.3626 8.87183 14.3626Z"
      fill="#66717E"
    />
  </svg>
);

const DisableNoteModal = ({ message, locale }) => (
  <Modal.ClassicOverlay dir="rtl" className={styles.disableNoteModal}>
    <div className={styles.disableNoteHeader}>
      <h3>{locale === "en" ? "Notice" : "تنبيه"}</h3>
      <Modal.CloseButton />
    </div>
    <div className={styles.disableNoteContent}>
      <p>{message}</p>
    </div>
    <div className={styles.disableNoteFooter}>
      <Button theme="primary" onClick={Modal.close}>
        {locale === "en" ? "Close" : "إغلاق"}
      </Button>
    </div>
  </Modal.ClassicOverlay>
);

const PricingPackage = ({ plan, isPopular }) => {
  const { token } = useAuthState();
  const router = useRouter();
  const { locale } = router;

  const {
    type,
    value,
    new_value: newValue,
    sub_pkg_type,
    action = "subscribe",
    description,
    disable_note_ar,
    disable_note_en,
    is_monthly_sub,
  } = plan || {};

  const { features, title } = JSON.parse(description)?.ar || {
    ar: {},
  };

  // Get the disable note based on locale
  const disableNote = locale === "en" ? disable_note_en : disable_note_ar;

  /* Show value as old value when it's not equal the new value */
  const oldValue = newValue && newValue !== value ? value : null;

  const handleSubmit = async (e) => {
    // If there's a disable note, show modal instead of proceeding
    if (disableNote) {
      Modal.open(<DisableNoteModal message={disableNote} locale={locale} />)(e);
      return;
    }

    if (!token) {
      return router.push(
        "/login?returnTo=" + encodeURIComponent(router.asPath),
      );
    }
    const { token: previewToken } = await getPreviewToken({
      type,
      pkgType: sub_pkg_type,
      action,
      title,
      value: newValue || value,
      contract_id: plan.contract_id,
      listing_id: plan.listing_id,
      option: plan.option,
    });
    return router.push({
      pathname: "/payment-gateway/checkout",
      query: { preview: previewToken },
    });
  };
  return (
    <Card
      className={`${styles.pricingCard} ${isPopular ? styles.popularCard : ""}`}
      key={title}
    >
      <div className={styles.titleRow}>
        <h1>{title}</h1>
        {isPopular && (
          <span className={styles.popularTag}>الأكثر استخداماً</span>
        )}
      </div>
      <div className={styles.priceSection}>
        <div className={styles.pkgValue}>
          {oldValue && (
            <span className={styles.oldValue}>
              {`${(oldValue || "").toLocaleString("en-GB")} ريال`}
            </span>
          )}
          <span className={styles.value}>
            {`${(newValue || value || "").toLocaleString("en-GB")} ريال`}
          </span>
          {/* Show monthly or yearly based on is_monthly_sub and it will show
          only for users who are already have monthly subscription */}
          {is_monthly_sub && !!token ? (
            <small>/شهري</small>
          ) : (
            <small>/سنوي</small>
          )}
        </div>
        <div className={styles.btnWrapper}>
          <Button
            theme="primary"
            className={styles.actionButton}
            onClick={handleSubmit}
          >
            {ACTION_MAP[action]}
          </Button>
        </div>
      </div>
      <ul className={styles.features}>
        {features?.map((feature) => {
          return (
            <li key={feature} className={styles.featureLine}>
              <CheckIcon />
              {feature}
            </li>
          );
        })}
      </ul>
    </Card>
  );
};

export default PricingPackage;
